import React, { useState } from "react";
import { Disc3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth, formatApiErrorDetail } from "@/auth/AuthContext";

export default function AuthModal() {
  const { showAuth, setShowAuth, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setError("");
    setPassword("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        const u = await login(email.trim(), password);
        toast.success(`Welcome back, ${u.name}`);
      } else {
        const u = await register(name.trim(), email.trim(), password);
        toast.success(`Account created — welcome, ${u.name}`);
      }
      setShowAuth(false);
      reset();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <Dialog open={showAuth} onOpenChange={setShowAuth}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-white max-w-sm" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display tracking-widest flex items-center gap-2">
            <Disc3 size={18} className="text-[#00e5ff]" />
            {mode === "login" ? "SIGN IN" : "CREATE ACCOUNT"}
          </DialogTitle>
          <DialogDescription className="text-neutral-500 text-xs">
            {mode === "login"
              ? "Sign in to save your songs to the cloud and open them on any device."
              : "Create a free account so your songs sync across desktop and phone."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col gap-3 mt-2">
          {mode === "register" && (
            <input
              data-testid="auth-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              className="bg-[#050505] border border-[#262626] rounded-sm px-3 py-2.5 text-sm outline-none focus:border-cyan-500 transition-colors"
            />
          )}
          <input
            data-testid="auth-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            autoComplete="email"
            className="bg-[#050505] border border-[#262626] rounded-sm px-3 py-2.5 text-sm outline-none focus:border-cyan-500 transition-colors"
          />
          <input
            data-testid="auth-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (min 6 characters)"
            required
            minLength={6}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            className="bg-[#050505] border border-[#262626] rounded-sm px-3 py-2.5 text-sm outline-none focus:border-cyan-500 transition-colors"
          />

          {error && (
            <p data-testid="auth-error" className="text-[#ff5c5c] text-xs">
              {error}
            </p>
          )}

          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={busy}
            className="flex items-center justify-center gap-2 bg-cyan-500 text-black font-bold rounded-sm py-2.5 text-sm hover:bg-cyan-400 transition-colors disabled:opacity-50"
          >
            {busy && <Loader2 size={15} className="animate-spin" />}
            {mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <button
          data-testid="auth-toggle-mode"
          onClick={() => {
            setMode(mode === "login" ? "register" : "login");
            reset();
          }}
          className="text-xs text-neutral-400 hover:text-[#00e5ff] transition-colors mt-1"
        >
          {mode === "login"
            ? "New here? Create an account"
            : "Already have an account? Sign in"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
